import { motion } from "framer-motion";
import { Check, ArrowUpRight, LogIn } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { plansData, faqData } from "@/data/mock";
import { SEO, PAGE_SEO } from "@/components/SEO";

const JOIN_URL = "https://app.lifely.guide/join/pricing";
const MEMBERS_AREA_URL = "https://app.lifely.guide";

export default function Pricing() {
  return (
    <div className="pt-24">
      <SEO {...PAGE_SEO.pricing} />
      {/* Header */}
      <section className="py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4"
          >
            Plans
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground"
          >
            Pricing
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="mt-4 text-lg text-muted-foreground max-w-xl"
          >
            Choose the level that fits how you want to use Lifely. Upgrade
            anytime.
          </motion.p>
          
          {/* Already a member sign in */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.6 }}
            className="mt-6"
          >
            <a
              href={MEMBERS_AREA_URL}
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <LogIn className="w-4 h-4" />
              Already a member? Sign in
            </a>
          </motion.div>
        </div>
      </section>

      {/* Horizontal Plans */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="space-y-10">
            {plansData.map((plan, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 + 0.3, duration: 0.6 }}
              >
                <div
                  className={`rounded-2xl border p-6 md:p-8 ${
                    plan.highlighted
                      ? "border-primary/25 shadow-lg ring-1 ring-primary/10 bg-card"
                      : "border-border/60 bg-card"
                  } hover:shadow-md transition-shadow duration-300`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-10">
                    {/* Plan Info - Left */}
                    <div className="lg:w-64 flex-shrink-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-xl font-semibold text-foreground">
                          {plan.name}
                        </h3>
                        {plan.highlighted && (
                          <span className="text-[0.65rem] uppercase tracking-wider bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                            Popular
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        {plan.tagline}
                      </p>
                      <div className="mb-4">
                        <span className="text-4xl font-serif text-foreground">
                          {plan.price}
                        </span>
                        {plan.priceNote && (
                          <span className="text-sm text-muted-foreground ml-1.5">
                            {plan.priceNote}
                          </span>
                        )}
                      </div>
                      
                      {/* CTA Button */}
                      <div className="hidden lg:block">
                        {plan.comingSoon ? (
                          <button
                            disabled
                            className="w-full rounded-full h-11 opacity-50 cursor-not-allowed border border-input bg-background px-4 py-2 text-sm font-medium"
                          >
                            {plan.cta}
                          </button>
                        ) : (
                          <div>
                            <a
                              href={JOIN_URL}
                              className={`w-full rounded-full h-11 inline-flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
                                plan.isPaid 
                                  ? "bg-foreground text-background hover:bg-foreground/85"
                                  : "border border-input bg-background hover:bg-accent hover:text-accent-foreground"
                              }`}
                            >
                              Get started
                              <ArrowUpRight className="w-4 h-4" />
                            </a>
                            <p className="text-xs text-muted-foreground mt-2 text-center">
                              Continue to members area
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Features - Right */}
                    <div className="flex-1">
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                        {plan.features.map((feature, j) => (
                          <li key={j} className="flex items-start gap-3">
                            <Check className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                            <span className="text-sm text-foreground/80">
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>
                      {plan.note && (
                        <p className="text-xs text-muted-foreground mt-4 pt-4 border-t border-border/40">
                          {plan.note}
                        </p>
                      )}
                    </div>

                    {/* Mobile CTA */}
                    <div className="lg:hidden">
                      {plan.comingSoon ? (
                        <button
                          disabled
                          className="w-full rounded-full h-11 opacity-50 cursor-not-allowed border border-input bg-background px-4 py-2 text-sm font-medium"
                        >
                          {plan.cta}
                        </button>
                      ) : (
                        <div>
                          <a
                            href={JOIN_URL}
                            className={`w-full rounded-full h-11 inline-flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
                              plan.isPaid 
                                ? "bg-foreground text-background hover:bg-foreground/85"
                                : "border border-input bg-background hover:bg-accent hover:text-accent-foreground"
                            }`}
                          >
                            Get started
                            <ArrowUpRight className="w-4 h-4" />
                          </a>
                          <p className="text-xs text-muted-foreground mt-2 text-center">
                            Continue to members area
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-secondary/40">
        <div className="max-w-2xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-serif text-3xl text-foreground text-center mb-12"
          >
            Frequently asked questions
          </motion.h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqData.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="bg-card border border-border/50 rounded-xl px-6 data-[state=open]:shadow-sm transition-shadow"
              >
                <AccordionTrigger className="text-left text-foreground font-medium py-5 hover:no-underline">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-5 leading-relaxed">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
